import {
	faChevronDown,
	faChevronRight,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { LLM_MODEL_PRICING } from '@supply-mover-2-0/shared';
import { useState, type ReactElement } from 'react';
import { Icon } from '../../components/base/Icon';
import { Button } from '../../components/forms/Button';
import { Input } from '../../components/forms/Input';
import { SingleSelect } from '../../components/forms/SingleSelect';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { ClampedNumberInput } from './ClampedNumberInput';
import type { AvailableDataSource } from './DynamicFieldValue';
import { DynamicFieldValue } from './DynamicFieldValue';

/**
 * Configuration form for the `email_ingest` step, presented as "Read Email".
 *
 * The step type key stays `email_ingest` (server registry, config keys,
 * validation); only the presentation is generic. The form is deliberately
 * small: which email to read, what to determine from it, and the limits
 * applied before anything is sent to the model.
 */

export interface EmailIngestAttachmentPolicy {
	mimeAllowlist?: Array<string>;
	maxAttachments?: number;
	maxBytesPerAttachment?: number;
	maxTotalBytes?: number;
	maxPdfPages?: number;
}

export interface EmailIngestStepConfigProps {
	config: {
		emailRecordId?: string;
		outcomeType?: string;
		includeBody?: boolean;
		attachmentPolicy?: EmailIngestAttachmentPolicy;
		model?: string;
		escalationModel?: string;
		escalationConfidenceThreshold?: number;
		spendBudget?: number;
		language?: string;
	};
	setConfig: (config: EmailIngestStepConfigProps['config']) => void;
	getAvailableDataSources?: () => Array<AvailableDataSource>;
	getDisplayText?: (value: string) => string;
}

/**
 * Only one outcome exists today. It is rendered as a select anyway so adding
 * the next one is a data change here rather than a new control.
 */
const OUTCOME_TYPE_OPTIONS = [
	{ value: 'order_sheet', label: 'Find a product request' },
];

/**
 * Mirrors `DEFAULT_ATTACHMENT_POLICY` on the server
 * (`apps/crm-api/src/modules/email-ingest/services/email-message-reader.service.ts`).
 * The step falls back to these when a value is absent, so the form shows them
 * as the effective settings rather than leaving the fields blank.
 */
const DEFAULT_POLICY: Required<EmailIngestAttachmentPolicy> = {
	mimeAllowlist: [
		'application/pdf',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'application/vnd.ms-excel',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'text/csv',
		'text/plain',
		'image/png',
		'image/jpeg',
		'image/gif',
		'image/webp',
	],
	maxAttachments: 10,
	maxBytesPerAttachment: 10485760,
	maxTotalBytes: 26214400,
	maxPdfPages: 50,
};

/**
 * The file types an operator picks by name. MIME strings are an implementation
 * detail of the mail protocol, so the form offers them grouped under the names
 * people actually use, and anything outside these groups is still reachable
 * through the "other file types" input below.
 */
const MIME_TYPE_GROUPS: Array<{
	id: string;
	label: string;
	mimeTypes: Array<string>;
}> = [
	{ id: 'pdf', label: 'PDF', mimeTypes: ['application/pdf'] },
	{
		id: 'word',
		label: 'Word (.docx)',
		mimeTypes: [
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		],
	},
	{
		id: 'excel',
		label: 'Excel (.xlsx, .xls)',
		mimeTypes: [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/vnd.ms-excel',
		],
	},
	{
		id: 'ods',
		label: 'OpenDocument Spreadsheet (.ods)',
		mimeTypes: ['application/vnd.oasis.opendocument.spreadsheet'],
	},
	{
		id: 'csv',
		label: 'CSV / TSV',
		mimeTypes: ['text/csv', 'application/csv', 'text/tab-separated-values'],
	},
	{ id: 'text', label: 'Plain text', mimeTypes: ['text/plain'] },
	{
		id: 'image',
		label: 'Images (PNG, JPEG, GIF, WebP)',
		mimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
	},
];

const GROUPED_MIME_TYPES = new Set(
	MIME_TYPE_GROUPS.flatMap((group) => group.mimeTypes),
);

/**
 * Mirrors the ladder defaults in
 * `apps/crm-api/src/modules/email-ingest/utils/extraction-escalation.ts`. The
 * form shows the effective setting rather than a blank control, and an unset
 * value on the server still resolves to the same three numbers.
 */
const DEFAULT_EXTRACTION_MODEL = 'claude-haiku-4-5';
const DEFAULT_ESCALATION_MODEL = 'claude-sonnet-5';
const DEFAULT_ESCALATION_CONFIDENCE_THRESHOLD = 0.4;
const MIN_ESCALATION_CONFIDENCE_THRESHOLD = 0;
const MAX_ESCALATION_CONFIDENCE_THRESHOLD = 1;

/**
 * The attachment limits are floors only: the step has no upper bound on a byte
 * size or a page count, so the fields are clamped from below and left open
 * above.
 */
const MIN_POLICY_COUNT = 1;

/**
 * The spend limit has no server default: an empty field means no limit, which
 * is what every flow written before the field existed already does. Only the
 * bounds of a limit that IS set are mirrored here.
 */
const MIN_SPEND_BUDGET = 0.01;
const MAX_SPEND_BUDGET = 100;

export function EmailIngestStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
}: EmailIngestStepConfigProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [policyExpanded, setPolicyExpanded] = useState(false);
	const [customMimeType, setCustomMimeType] = useState('');

	const policy = config.attachmentPolicy ?? {};

	const updateConfig = <K extends keyof EmailIngestStepConfigProps['config']>(
		key: K,
		value: EmailIngestStepConfigProps['config'][K],
	): void => {
		setConfig({ ...config, [key]: value });
	};

	const updatePolicy = <K extends keyof EmailIngestAttachmentPolicy>(
		key: K,
		value: EmailIngestAttachmentPolicy[K],
	): void => {
		setConfig({
			...config,
			attachmentPolicy: { ...policy, [key]: value },
		});
	};

	/**
	 * An absent list means "the server defaults apply", so the form shows those
	 * boxes checked. The first edit writes the whole list back explicitly, which
	 * is what makes a later change to the server defaults visible as a change
	 * rather than silently rewriting a step someone already tuned.
	 */
	const allowlist = policy.mimeAllowlist ?? DEFAULT_POLICY.mimeAllowlist;
	const selectedMimeTypes = new Set(
		allowlist.map((mimeType) => mimeType.toLowerCase()),
	);
	const customMimeTypes = allowlist.filter(
		(mimeType) => !GROUPED_MIME_TYPES.has(mimeType.toLowerCase()),
	);

	const isGroupChecked = (mimeTypes: Array<string>): boolean =>
		mimeTypes.every((mimeType) => selectedMimeTypes.has(mimeType));

	const toggleMimeGroup = (
		mimeTypes: Array<string>,
		checked: boolean,
	): void => {
		const remaining = allowlist.filter(
			(entry) => !mimeTypes.includes(entry.toLowerCase()),
		);

		updatePolicy(
			'mimeAllowlist',
			checked ? [...remaining, ...mimeTypes] : remaining,
		);
	};

	const addCustomMimeType = (): void => {
		const entry = customMimeType.trim();

		if (entry === '') {
			return;
		}

		setCustomMimeType('');

		if (selectedMimeTypes.has(entry.toLowerCase())) {
			return;
		}

		updatePolicy('mimeAllowlist', [...allowlist, entry]);
	};

	const removeCustomMimeType = (mimeType: string): void => {
		updatePolicy(
			'mimeAllowlist',
			allowlist.filter((entry) => entry !== mimeType),
		);
	};

	/**
	 * The offered models come from the shared price table, so a model that can
	 * be chosen here is always a model the spend reporting can price. The table
	 * is ordered cheapest-first and is rendered in that order.
	 */
	const modelOptions = LLM_MODEL_PRICING.map((pricing) => ({
		value: pricing.modelId,
		label: t(pricing.label),
	}));

	return (
		<div className="space-y-6">
			<div>
				<h3
					className={`text-base font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('Read Email')}
				</h3>

				{/* Email record to read */}
				<div className="mb-4">
					<DynamicFieldValue
						label={t('Email Record ID')}
						value={config.emailRecordId ?? ''}
						onChange={(value) => {
							updateConfig('emailRecordId', value);
						}}
						dataType="string"
						required={true}
						placeholder={t('Select the Email Record ID')}
						description={t('Choose the Email Record ID to read.')}
						availableData={getAvailableDataSources?.() ?? []}
						displayTransform={getDisplayText}
					/>
				</div>

				{/* Outcome type */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('What to Determine')}
					</label>
					<SingleSelect
						name="email-ingest-outcome-type"
						value={config.outcomeType ?? 'order_sheet'}
						onChange={(value) => {
							updateConfig('outcomeType', value);
						}}
						options={OUTCOME_TYPE_OPTIONS.map((option) => ({
							value: option.value,
							label: t(option.label),
						}))}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('What to read out of the message.')}
					</p>
				</div>

				{/* Include message body */}
				<div className="mb-4">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={config.includeBody !== false}
							onChange={(e) => {
								updateConfig('includeBody', e.target.checked);
							}}
						/>
						<span
							className={`text-sm font-medium ${colors.classes.textPrimary}`}
						>
							{t('Read the message body')}
						</span>
					</label>
					<p className={`ml-6 text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Reads the body as well as the attachments. Links and remote images are removed first.',
						)}
					</p>
				</div>

				{/* Model. The step runs only through the Anthropic task service, so
				    there is no provider choice — Claude models only. */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Model')}
					</label>
					<SingleSelect
						name="email-ingest-model"
						value={config.model ?? DEFAULT_EXTRACTION_MODEL}
						onChange={(value) => {
							updateConfig('model', value === '' ? undefined : value);
						}}
						options={modelOptions}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'The model that reads every document first. Reading an order sheet is mechanical, so the fastest model handles most messages.',
						)}
					</p>
				</div>

				{/* Escalation model — the one retry for a document that read badly. */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Escalation Model')}
					</label>
					<SingleSelect
						name="email-ingest-escalation-model"
						value={config.escalationModel ?? DEFAULT_ESCALATION_MODEL}
						onChange={(value) => {
							updateConfig('escalationModel', value === '' ? undefined : value);
						}}
						options={modelOptions}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'A document that reads badly is re-read once on this model. Set it to the same model as above to switch escalation off.',
						)}
					</p>
				</div>

				{/* Escalation threshold */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Escalation Confidence Threshold')}
					</label>
					{/* A fraction, so it accepts decimals: clamping while it is typed
					    would read "0." as 0 and lose the digits that follow. */}
					<ClampedNumberInput
						value={config.escalationConfidenceThreshold}
						fallback={DEFAULT_ESCALATION_CONFIDENCE_THRESHOLD}
						min={MIN_ESCALATION_CONFIDENCE_THRESHOLD}
						max={MAX_ESCALATION_CONFIDENCE_THRESHOLD}
						integer={false}
						step={0.05}
						onCommit={(value) => {
							updateConfig('escalationConfidenceThreshold', value);
						}}
						hint={t(
							'Between 0 and 1. A document is re-read on the escalation model when the median confidence of its lines falls below this number, when nothing could be read out of it, or when the first attempt failed. Each document escalates at most once. Default 0.4.',
						)}
					/>
				</div>

				{/* The cost control, next to the models it bounds: what a model costs
				    and how much of it may be spent are one decision. */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Spend Budget (USD)')}
					</label>
					{/* Money, so it accepts decimals: $0.25 is a sensible limit for a
					    mailbox of short messages. */}
					<ClampedNumberInput
						value={config.spendBudget}
						min={MIN_SPEND_BUDGET}
						max={MAX_SPEND_BUDGET}
						integer={false}
						step={0.05}
						placeholder={t('No limit')}
						onCommit={(value) => {
							updateConfig('spendBudget', value);
						}}
						hint={t(
							'The most this step may spend reading one email, across every attachment and every escalation. The reader stops before a call that would go past it and reports what it left unread. Leave it empty for no limit.',
						)}
					/>
				</div>

				{/* Optional source language hint */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Source Language')}
					</label>
					<Input
						value={config.language ?? ''}
						onChange={(e) => {
							updateConfig(
								'language',
								e.target.value === '' ? undefined : e.target.value,
							);
						}}
						placeholder={t('For example: German')}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('Optional hint about the language of the message.')}
					</p>
				</div>
			</div>

			{/* Attachment limits — advanced, collapsed by default */}
			<div
				className={`border rounded-lg ${colors.classes.borderDefault} overflow-hidden`}
			>
				<button
					type="button"
					onClick={() => {
						setPolicyExpanded(!policyExpanded);
					}}
					className={`w-full flex items-center gap-2 px-3 py-2.5 text-left ${colors.classes.bgSecondary}`}
				>
					<Icon
						icon={policyExpanded ? faChevronDown : faChevronRight}
						className={`text-xs ${colors.classes.textSecondary}`}
					/>
					<span className={`text-sm font-medium ${colors.classes.textPrimary}`}>
						{t('Attachment Limits')}
					</span>
				</button>

				{policyExpanded && (
					<div className="p-3 space-y-4">
						<p className={`text-xs ${colors.classes.textSecondary}`}>
							{t(
								'Applied before anything is read. Attachments over a limit are skipped and listed in the step warnings.',
							)}
						</p>

						{/* Allowed file types */}
						<div>
							<label
								className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
							>
								{t('Allowed File Types')}
							</label>
							<div className="space-y-2">
								{MIME_TYPE_GROUPS.map((group) => (
									<label
										key={group.id}
										className="flex items-center gap-2 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={isGroupChecked(group.mimeTypes)}
											onChange={(e) => {
												toggleMimeGroup(group.mimeTypes, e.target.checked);
											}}
										/>
										<span className={`text-sm ${colors.classes.textPrimary}`}>
											{t(group.label)}
										</span>
									</label>
								))}
							</div>
							<p className={`text-xs mt-2 ${colors.classes.textSecondary}`}>
								{t(
									'Any attachment of another type is skipped and listed in the step warnings.',
								)}
							</p>

							{/* Clearing every box stores an empty list, and the step reads an
							    empty list as "no preference" and applies its own defaults.
							    Without this the form reads as "nothing is allowed" while the
							    step accepts the default set. */}
							{allowlist.length === 0 ? (
								<p className={`text-xs mt-2 ${colors.classes.textWarning}`}>
									{t(
										'No file types are selected, so the default set is accepted: PDF, Word, Excel, CSV, plain text and images.',
									)}
								</p>
							) : null}

							{/* Anything the checkboxes do not cover, kept reachable. */}
							<div className="mt-3">
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Other File Types')}
								</label>
								{customMimeTypes.length > 0 && (
									<div className="flex flex-wrap gap-2 mb-2">
										{customMimeTypes.map((mimeType) => (
											<span
												key={mimeType}
												className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${colors.classes.bgSecondary} ${colors.classes.textPrimary}`}
											>
												{t(mimeType)}
												<button
													type="button"
													onClick={() => {
														removeCustomMimeType(mimeType);
													}}
													aria-label={t('Remove')}
													className={colors.classes.textSecondary}
												>
													<Icon icon={faXmark} className="text-xs" />
												</button>
											</span>
										))}
									</div>
								)}
								<div className="flex items-center gap-2">
									<div className="flex-1">
										<Input
											value={customMimeType}
											onChange={(e) => {
												setCustomMimeType(e.target.value);
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													// The config form sits inside the flow builder;
													// without this the key would bubble up and submit it.
													e.preventDefault();
													addCustomMimeType();
												}
											}}
											placeholder={t('For example: application/rtf')}
										/>
									</div>
									<Button
										type="button"
										variant="secondary"
										size="sm"
										onClick={addCustomMimeType}
										disabled={customMimeType.trim() === ''}
									>
										{t('Add')}
									</Button>
								</div>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t(
										'Add a MIME type that is not listed above. It is allowed, but only PDFs, Word documents, spreadsheets, images and plain text can actually be read.',
									)}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							{/* Max attachments */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Attachments')}
								</label>
								<ClampedNumberInput
									value={policy.maxAttachments}
									fallback={DEFAULT_POLICY.maxAttachments}
									min={MIN_POLICY_COUNT}
									onCommit={(value) => {
										updatePolicy('maxAttachments', value);
									}}
									hint={t('Default 10.')}
								/>
							</div>

							{/* Max PDF pages */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum PDF Pages')}
								</label>
								<ClampedNumberInput
									value={policy.maxPdfPages}
									fallback={DEFAULT_POLICY.maxPdfPages}
									min={MIN_POLICY_COUNT}
									onCommit={(value) => {
										updatePolicy('maxPdfPages', value);
									}}
									hint={t('Longer PDFs are skipped. Default 50.')}
								/>
							</div>

							{/* Per-attachment bytes */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Size Per Attachment')}
								</label>
								<ClampedNumberInput
									value={policy.maxBytesPerAttachment}
									fallback={DEFAULT_POLICY.maxBytesPerAttachment}
									min={MIN_POLICY_COUNT}
									onCommit={(value) => {
										updatePolicy('maxBytesPerAttachment', value);
									}}
									hint={t('In bytes. Default 10485760 (10 MB).')}
								/>
							</div>

							{/* Total bytes */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Size For All Attachments')}
								</label>
								<ClampedNumberInput
									value={policy.maxTotalBytes}
									fallback={DEFAULT_POLICY.maxTotalBytes}
									min={MIN_POLICY_COUNT}
									onCommit={(value) => {
										updatePolicy('maxTotalBytes', value);
									}}
									hint={t('In bytes. Default 26214400 (25 MB).')}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
